/**
 * Reviewing cached cloud-order conflicts: apply the offline change, or discard it.
 *
 * A conflict is an offline change whose cloud copy moved before it could be
 * replayed. The app refuses to guess which version wins, so an admin decides.
 *
 * Loading stays coordinated across all three review workflows, by owner
 * decision, so this hook exposes `fetchItems` for the screen's single loader
 * rather than loading on its own. Both conflict kinds are fetched here and
 * merged, because to a reviewer they are one queue.
 *
 * See TCA-26081204.
 */
import { useMemo, useState } from 'react';
import { useReviewActions } from './useReviewActions';

import {
  applyCachedOrderFieldConflict,
  applyCachedOrderStatusConflict,
  getCachedOrderFieldConflicts,
  getCachedOrderStatusConflicts,
  getOrder,
  refreshCachedOrderFieldConflictEvidence,
  refreshCachedOrderStatusConflictEvidence,
  beginCachedEditDecision, settleCachedEditDecision, getOfflineEditCapabilities,
} from '@/api/orders';
import { getUsers } from '@/api/users';

const FIELD_EDIT = 'cloud_order_field_edit';

const buildUserLookup = (users) => {
  const lookup = {};
  (users || []).forEach((user) => {
    if (!user?.id) return;
    lookup[user.id] = user.name || user.username || 'Unknown user';
  });
  return lookup;
};

export function useCachedOrderConflictReview({ reload, owner }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);

  const act = useReviewActions(owner, reload);
  const userNames = useMemo(() => buildUserLookup(users), [users]);

  /**
   * Re-read each conflict's cloud side so the reviewer decides against current
   * facts rather than whatever was true when the conflict was recorded.
   *
   * Best effort on purpose: a stale but visible conflict is safer than one
   * hidden or blocked because a refresh call failed.
   */
  const refreshEvidence = async (conflictItems, read) => Promise.all(
    (conflictItems || []).map(async (item) => {
      const mutation = item.mutation || {};
      const mutationId = mutation.id;
      const orderId = mutation.target_id;
      if (mutation.state === 'failed_terminal') return item;
      if (!mutationId || !orderId) return item;
      try {
        const cloudOrder = await read.cloud(getOrder, orderId);
        const refresh = mutation.operation_type === FIELD_EDIT
          ? await read.evidence(refreshCachedOrderFieldConflictEvidence, mutationId, cloudOrder)
          : await read.evidence(refreshCachedOrderStatusConflictEvidence, mutationId, cloudOrder);
        return {
          ...item,
          mutation: {
            ...mutation,
            cloud_order_snapshot: refresh.cloud_order_snapshot,
            cloud_order_snapshot_refreshed_at:
              refresh.cloud_order_snapshot_refreshed_at,
          },
        };
      } catch {
        return item;
      }
    }),
  );

  /** Both kinds, in the order the screen has always merged them. */
  const fetchItems = async (read) => {
    const [statusConflicts, fieldConflicts] = await Promise.all([
      read.local(getCachedOrderStatusConflicts),
      read.local(getCachedOrderFieldConflicts),
    ]);
    if (!Array.isArray(statusConflicts) || !Array.isArray(fieldConflicts)) throw new Error('Invalid conflict queues');
    return [...statusConflicts, ...fieldConflicts];
  };

  /**
   * Names are only needed to render who did what on a conflict, so they are
   * fetched only when there is a conflict to render, and cleared otherwise.
   */
  const loadUserNames = async (conflictItems, read) => {
    const next = conflictItems.length ? await read.cloud(getUsers, undefined).catch(() => []) : [];
    if (read.current()) setUsers(next);
  };
  const decide = (item, apply) => {
    const mutation = item.mutation || {};
    if (mutation.state === 'submission_uncertain' || mutation.predecessor_id) return false;
    if (!mutation.id || !window.confirm(apply
      ? 'Replace the newer online order information with this offline change?'
      : 'Discard this offline status change?')) return false;
    const field = mutation.operation_type === FIELD_EDIT;
    let submitted;
    return act(item, {
      key: `mutation:${mutation.id}`, start: () => setResolvingId(mutation.id), finish: () => setResolvingId(null),
      prepare: async config => {
        submitted = await beginCachedEditDecision(mutation.id, mutation.edit_revision || 1,
          apply ? 'use_offline_change' : 'discard_offline_change', config, mutation.cloud_order_snapshot);
        return submitted;
      },
      cloud: apply ? async (config, prepared) => {
        const capabilities = await getOfflineEditCapabilities(config);
        if (capabilities?.edit_protocol !== 2 || capabilities?.review_compare !== true || capabilities?.ledger_generation !== 1) {
          const error = new Error('The cloud must be updated before reviewing this edit.');
          error.response = { status: 409, data: { detail: error.message } }; throw error;
        }
        return (field ? applyCachedOrderFieldConflict : applyCachedOrderStatusConflict)(
          prepared, config);
      } : undefined,
      validateCloud: value => value?.applied === true,
      validateLocal: value => value?.state === (apply ? 'applied' : 'discarded'),
      local: (config, result) => settleCachedEditDecision(mutation.id, submitted.review_attempt, result?.cloud_order, false, config),
      refused: config => settleCachedEditDecision(mutation.id, submitted.review_attempt, null, true, config),
      reason: apply ? 'offline_conflict_resolved' : 'offline_conflict_discarded',
      message: apply ? 'Offline change applied' : 'Offline change discarded',
      failure: apply ? 'Failed to apply offline change' : 'Failed to discard offline change',
    });
  };
  return { items, setItems, fetchItems, refreshEvidence, loadUserNames, userNames, resolvingId,
    onUseOfflineChange: item => decide(item, true), onDiscard: item => decide(item, false) };
}
export default useCachedOrderConflictReview;
